// Small pressed-theme building blocks for Home: the highlighter Mark, band
// chip, ink date stamp, game glyph (name → line icon), and the printed
// SetCover object. Ported from specs/home-page-july6.

import type { ReactNode } from 'react'
import { useCssVars, type CssVars } from '@/hooks/useCssVars'
import type { HomeBand } from './homeSets'
import './styles/HomePieces.css'

const BAND_LABEL: Record<HomeBand, string> = {
  comfort: 'Comfort',
  just: 'Just Right',
  growth: 'Growth',
  reach: 'Reach',
}
const SET_ACCENT: Record<HomeBand, string> = {
  just: 'var(--coral)',
  growth: 'var(--blue)',
  reach: 'var(--ink)',
  comfort: 'var(--green)',
}
const SET_BEHIND: Record<HomeBand, string> = {
  just: 'var(--yellow)',
  growth: 'var(--coral)',
  reach: 'var(--coral)',
  comfort: 'var(--yellow)',
}

type HomePiecesStyle = CssVars & {
  '--home-mark-height'?: string
  '--home-mark-bottom'?: string
  '--home-band-color'?: string
  '--home-cover-w'?: string
  '--home-cover-h'?: string
  '--home-cover-f'?: number
  '--home-cover-accent'?: string
  '--home-cover-behind'?: string
}

// ── Highlighter swipe — the one-per-screen brand signature ───────────────────
export function Mark({ children, h = 14, bottom = 3 }: { children: ReactNode; h?: number; bottom?: number }) {
  const markRef = useCssVars<HTMLSpanElement>({
    '--home-mark-height': `${h}px`,
    '--home-mark-bottom': `${bottom}px`,
  })

  return (
    <span ref={markRef} className="home-pieces__mark">
      <span aria-hidden="true" className="home-pieces__mark-swipe" />
      <span className="home-pieces__mark-copy">{children}</span>
    </span>
  )
}

// ── Band chip ────────────────────────────────────────────────────────────────
export function BandChip({ band }: { band: HomeBand }) {
  const dotRef = useCssVars<HTMLSpanElement>({
    '--home-band-color': `var(--band-${band})`,
  })

  return (
    <span className="home-pieces__band-chip">
      <span ref={dotRef} className="home-pieces__band-dot" />
      {BAND_LABEL[band]}
    </span>
  )
}

// ── Date stamp ───────────────────────────────────────────────────────────────
export function DateStamp({ text }: { text: string }) {
  return (
    <span className="home-pieces__date-stamp">
      <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="14" height="12" rx="1.5" /><path d="M3 8h14M7 2.5v3M13 2.5v3" /></svg>
      {text}
    </span>
  )
}

// ── Game glyph — map a game name to a line icon ─────────────────────────────
type GlyphKind =
  | 'grid' | 'cards' | 'stones' | 'chain' | 'dice' | 'gap' | 'check' | 'swap'
  | 'link' | 'plus' | 'puzzle' | 'search' | 'seq' | 'chat' | 'sort' | 'hash'

const GAME_GLYPH: Record<string, GlyphKind> = {
  'Word Bingo': 'grid', 'Bingo': 'grid',
  'Sentence Cards': 'cards', 'Phrase Builder': 'cards',
  'Because / But / So': 'stones', 'Because But So': 'stones',
  'Word Chains': 'chain', 'Roll & Read': 'dice', 'Roll and Read': 'dice',
  'Fill the Gap': 'gap', 'True or False': 'check',
  'Sentence Unscramble': 'swap', 'Meaning Match': 'link', 'Match and Cover': 'link',
  'Synonym Match': 'link', 'Multiple Meaning Match': 'link',
  'Word Sums': 'plus', 'Compound Puzzle': 'puzzle', 'Compound Puzzles': 'puzzle',
  'Meaning Word Search': 'search', 'Word Search': 'search', 'Spot the Nonsense': 'search',
  'Sequence Cards': 'seq', 'Word Riddles': 'chat',
  'Word Association Sort': 'sort', 'Sentence Tic-Tac-Toe': 'hash', 'Four in a Row': 'grid',
}

function glyphFor(name: string): GlyphKind {
  return GAME_GLYPH[name] ?? 'grid'
}

export function GameGlyph({ name, size = 17 }: { name: string; size?: number }) {
  const kind = glyphFor(name)
  const paths: Record<GlyphKind, ReactNode> = {
    grid: <><rect x="2.8" y="2.8" width="14.4" height="14.4" rx="2" /><path d="M10 2.8v14.4M2.8 10h14.4" /></>,
    cards: <><rect x="2.5" y="6" width="10.5" height="10.5" rx="1.6" /><path d="M6 6V3.5h10.5V14" /></>,
    stones: <><rect x="3" y="3.4" width="14" height="3.3" rx="1.65" /><rect x="3" y="8.35" width="14" height="3.3" rx="1.65" /><rect x="3" y="13.3" width="10" height="3.3" rx="1.65" /></>,
    chain: <><rect x="2.4" y="7" width="8.6" height="6" rx="3" /><rect x="9" y="7" width="8.6" height="6" rx="3" /></>,
    dice: <><rect x="3" y="3" width="14" height="14" rx="3" /><circle cx="7" cy="7" r="1.15" fill="currentColor" stroke="none" /><circle cx="10" cy="10" r="1.15" fill="currentColor" stroke="none" /><circle cx="13" cy="13" r="1.15" fill="currentColor" stroke="none" /></>,
    gap: <><path d="M3.5 7.8h13M3.5 12.4h3.5M12.9 12.4h3.6" /><rect x="8" y="10.6" width="4" height="3.6" rx="1" /></>,
    check: <><circle cx="10" cy="10" r="7.2" /><path d="M6.6 10.2l2.4 2.4 4.5-5" /></>,
    swap: <><path d="M4 8h11M12 5l3 3-3 3" /><path d="M16 13H5M8 16l-3-3 3-3" /></>,
    link: <><circle cx="7" cy="10" r="3.3" /><circle cx="13" cy="10" r="3.3" /><path d="M9.6 10h0.8" /></>,
    plus: <><circle cx="10" cy="10" r="7.2" /><path d="M6.4 10h7.2M10 6.4v7.2" /></>,
    puzzle: <path d="M5 6.5A1.5 1.5 0 016.5 5h1.7a1.4 1.4 0 012.8 0h1.7A1.5 1.5 0 0115 6.5v1.7a1.4 1.4 0 010 2.8v2.5A1.5 1.5 0 0113.5 15H6.5A1.5 1.5 0 015 13.5V11.2a1.4 1.4 0 000-2.8z" />,
    search: <><circle cx="8.8" cy="8.8" r="5.2" /><path d="M12.8 12.8 17 17" /></>,
    seq: <><circle cx="4" cy="6" r="1.1" fill="currentColor" stroke="none" /><path d="M7.5 6H17" /><circle cx="4" cy="10" r="1.1" fill="currentColor" stroke="none" /><path d="M7.5 10H17" /><circle cx="4" cy="14" r="1.1" fill="currentColor" stroke="none" /><path d="M7.5 14h7" /></>,
    chat: <path d="M3.6 5.6h12.8v8h-6l-3.4 3v-3H3.6z" />,
    sort: <><path d="M4 6h9M4 10h6M4 14h3.5" /><path d="M14.5 8v8M12 13.5l2.5 2.5 2.5-2.5" /></>,
    hash: <path d="M7 3.5v13M13 3.5v13M3.5 7h13M3.5 13h13" />,
  }
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {paths[kind]}
    </svg>
  )
}

// ── Cover icon (a small lino-cut mark centered on the cover) ────────────────
const COVER_ICONS = ['leaf', 'moon', 'cat', 'web', 'bolt', 'star'] as const
type CoverIconName = (typeof COVER_ICONS)[number]

function coverIconFor(id: string): CoverIconName {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffff
  return COVER_ICONS[h % COVER_ICONS.length]
}

function CoverIcon({ name, size }: { name: CoverIconName; size: number }) {
  const paths: Record<CoverIconName, ReactNode> = {
    leaf: <path d="M4 16c0-7 5-12 12-12 0 7-5 12-12 12zM8 12l6-6" />,
    moon: <path d="M15 12.5A6 6 0 118 4a5 5 0 007 8.5z" />,
    cat: <path d="M5 8l-1-3 3 2h6l3-2-1 3v5a4 4 0 01-4 4H9a4 4 0 01-4-4z M8 11h.01M12 11h.01" />,
    web: <><circle cx="10" cy="10" r="7" /><path d="M10 3v14M3 10h14M5 5l10 10M15 5L5 15" /></>,
    bolt: <path d="M11 2 4 11h5l-1 7 7-9h-5z" />,
    star: <path d="M10 2.5l2.3 4.7 5.2.8-3.8 3.7.9 5.2-4.6-2.5-4.6 2.5.9-5.2L2.5 8l5.2-.8z" />,
  }
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="var(--paper-2)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  )
}

// ── SetCover — the printed-book object ──────────────────────────────────────
export function SetCover({ id, title, author, band, fromYou, w = 150, standing }: {
  id: string
  title: string
  author: string | null
  band: HomeBand | null
  fromYou?: boolean
  w?: number
  standing?: boolean
}) {
  const h = Math.round(w * 1.24)
  const f = w / 150
  const accent = band ? SET_ACCENT[band] : 'var(--ink)'
  const behind = band ? SET_BEHIND[band] : 'var(--yellow)'
  const icon = coverIconFor(id)
  const coverVars = {
    '--home-cover-w': `${w}px`,
    '--home-cover-h': `${h}px`,
    '--home-cover-f': f,
    '--home-cover-accent': accent,
    '--home-cover-behind': behind,
  } as HomePiecesStyle
  const coverRef = useCssVars<HTMLDivElement>(coverVars)

  return (
    <div ref={coverRef} className="home-pieces__set-cover">
      {standing && <div className="home-pieces__set-cover-shadow" />}
      <div className="home-pieces__set-cover-sheet home-pieces__set-cover-sheet--back" />
      <div className="home-pieces__set-cover-sheet home-pieces__set-cover-sheet--middle" />
      <div className="home-pieces__set-cover-front">
        <div className="home-pieces__set-cover-spine" />
        <div className="home-pieces__set-cover-spine-shadow" />
        {author && <div className="home-pieces__set-cover-author">{author}</div>}
        <div className="home-pieces__set-cover-icon"><CoverIcon name={icon} size={44 * f} /></div>
        <div className="home-pieces__set-cover-title">{title}</div>
      </div>
      {fromYou && (
        <div className="home-pieces__from-you">
          <span className="home-pieces__from-you-copy">from you</span>
        </div>
      )}
    </div>
  )
}
